import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CircularProgress from '@mui/material/CircularProgress';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import * as XLSX from 'xlsx';
import { useAuth0 } from '@auth0/auth0-react';
import { GeneratedFile, FileRef, ChatFileData } from '../../shared/interfaces';
import {
  fetchChatFile,
  saveChatFileContent,
  exportChatFile,
  downloadChatFileBlob,
} from '../../shared/services/chatFilesApi';
import SpreadsheetEditor from './SpreadsheetEditor';
import RichTextEditor from './RichTextEditor';

const CustomTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '0.875rem',
  },
});

// Main container for file preview — light inline pill, aligned with
// the tool indicator style. No heavy card; the file is one of several
// inline-rendered "things the model did" within a message.
const FilePreviewContainer = styled.div<{ $bare?: boolean }>`
  margin: 6px 0;
  border-radius: 10px;
  overflow: ${({ $bare }) => ($bare ? 'visible' : 'hidden')};
  background: transparent;
  border: ${({ $bare, theme }) =>
    $bare ? 'none' : `1px solid ${theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`};
  display: inline-block;
  max-width: 100%;
`;

// Header with file icon, name, and actions. No background fill, no
// separator below — the row itself is the file representation.
const FileHeader = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 10px 6px 8px;
  background: transparent;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 120ms ease;
  &:hover {
    background: ${({ $clickable, theme }) => ($clickable ? (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.025)') : 'transparent')};
  }
`;

const FileHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FileIconWrapper = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: ${({ $color }) => $color}15;
  color: ${({ $color }) => $color};
  flex-shrink: 0;
  & svg { font-size: 15px; }
`;

const FileName = styled.span`
  font-size: 0.82rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
  font-family: 'Sora', 'Lexend', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
`;

const FileActions = styled.div`
  display: flex;
  gap: 4px;
`;

const ActionButton = styled(IconButton)`
  && {
    padding: 4px;
    color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.5)'};
    & svg { font-size: 16px; }
    &:hover {
      background: ${({ theme }) => theme.hover_overlay_light};
      color: ${({ theme }) => theme.font_color};
    }
  }
`;

// Content area for previews
const PreviewContent = styled.div`
  padding: 0;
  max-height: 400px;
  overflow: auto;
`;

// Table styles for spreadsheet preview
const TableWrapper = styled.div`
  overflow-x: auto;
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const TableHeader = styled.th`
  padding: 10px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
  border-bottom: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'};
  }
  &:hover {
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)'};
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  color: ${({ theme }) => theme.font_color};
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowNumber = styled(TableCell)`
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'};
  font-size: 0.8rem;
  width: 40px;
  text-align: center;
`;

// Footer with preview info
const PreviewFooter = styled.div`
  padding: 10px 16px;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
  border-top: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
`;

const ClickableText = styled.span`
  color: ${({ theme }) => theme.mode === 'dark' ? '#60a5fa' : '#2563eb'};
  cursor: pointer;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

// Image preview styles
const ImagePreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  background: transparent;
  cursor: pointer;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 1px 6px ${({ theme }) => theme.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.08)'};
`;

// Image-only preview: relative box so the download control can overlay the
// image (no filename header, no gray card).
const ImageBlock = styled.div`
  position: relative;
  display: inline-flex;
  max-width: 100%;
`;

const ImageDownloadButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #fff;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 120ms ease, background 120ms ease;
  &:hover { background: rgba(0, 0, 0, 0.55); }

  ${ImageBlock}:hover & {
    opacity: 1;
  }
`;

// PDF/Other file placeholder
const FilePlaceholder = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: ${({ theme }) => theme.mode === 'dark' ? '#0a0a0a' : '#f8f9fa'};
  ${({ $clickable }) => $clickable && `cursor: pointer;`}
`;

const PlaceholderIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ $color }) => $color}15;
  color: ${({ $color }) => $color};
  margin-bottom: 12px;
`;

const PlaceholderText = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
`;

// Modal styles
const ModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  background: ${({ theme }) => theme.mode === 'dark' ? '#1a1a1a' : '#ffffff'};
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.3);
  outline: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border-bottom: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalFileName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const ModalContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0;
`;

const ModalImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
  background: ${({ theme }) => theme.mode === 'dark' ? '#0a0a0a' : '#f5f5f5'};
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
`;

const ModalTableWrapper = styled.div`
  overflow: auto;
  max-height: 70vh;
`;

const EditorModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  background: ${({ theme }) => theme.mode === 'dark' ? '#1a1a1a' : '#ffffff'};
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.3);
  outline: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EditorContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ExportMenuItem = styled(MenuItem)`
  && {
    padding: 10px 16px;
    gap: 12px;
  }
`;

// Helper functions
const getFileColor = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return '#10b981';
  if (mimeType === 'application/pdf') return '#ef4444';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') return '#22c55e';
  if (mimeType.includes('word') || mimeType.includes('document')) return '#3b82f6';
  if (mimeType.startsWith('text/')) return '#8b5cf6';
  return '#6b7280';
};

const getFileIcon = (mimeType: string, size: number = 20) => {
  if (mimeType.startsWith('image/')) return <InsertPhotoIcon style={{ fontSize: size }} />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon style={{ fontSize: size }} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') return <TableChartIcon style={{ fontSize: size }} />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <ArticleIcon style={{ fontSize: size }} />;
  if (mimeType.startsWith('text/')) return <ArticleIcon style={{ fontSize: size }} />;
  return <InsertDriveFileIcon style={{ fontSize: size }} />;
};

const getFileName = (path: string): string => {
  return path.split('/').pop() || path;
};

const isSpreadsheet = (mimeType: string): boolean => {
  return mimeType === 'text/csv' ||
         mimeType.includes('spreadsheet') ||
         mimeType.includes('excel') ||
         mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
         mimeType === 'application/vnd.ms-excel';
};

const isImage = (mimeType: string): boolean => mimeType.startsWith('image/');

const isTextDocument = (mimeType: string): boolean => {
  return mimeType === 'text/plain' ||
         mimeType === 'text/html' ||
         mimeType === 'text/markdown' ||
         mimeType.includes('word') ||
         mimeType.includes('document');
};

// True only for formats where base64 → string via atob() is meaningful (actual text, not binary)
const isDecodableAsText = (mimeType: string): boolean => {
  return mimeType === 'text/plain' ||
         mimeType === 'text/html' ||
         mimeType === 'text/markdown';
};

// Convert plain text (with \n paragraph separators) to HTML for the TipTap editor
const plainTextToEditorHtml = (text: string): string => {
  if (text.startsWith('<p>') || text.startsWith('<h')) return text; // already HTML
  return text
    .split('\n')
    .map(line => `<p>${line || '<br>'}</p>`)
    .join('');
};

// Convert TipTap HTML back to plain text for storage/export
const editorHtmlToPlainText = (html: string): string => {
  if (!html.includes('<')) return html; // already plain text
  const div = document.createElement('div');
  div.innerHTML = html;
  const paragraphs: string[] = [];
  div.querySelectorAll('p, h1, h2, h3').forEach(el => {
    paragraphs.push(el.textContent || '');
  });
  return paragraphs.length > 0 ? paragraphs.join('\n') : div.textContent || '';
};

// Parse CSV data from base64
const parseCSV = (base64: string): { headers: string[]; rows: string[][] } => {
  try {
    const decoded = atob(base64);
    const lines = decoded.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    // Simple CSV parsing (handles basic cases)
    const parseLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if ((char === ',' || char === ';') && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseLine(lines[0]);
    const rows = lines.slice(1).map(parseLine);

    return { headers, rows };
  } catch (e) {
    console.error('Error parsing CSV:', e);
    return { headers: [], rows: [] };
  }
};

// Parse Excel data from base64
const parseExcel = (base64: string): { headers: string[]; rows: string[][] } => {
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const workbook = XLSX.read(bytes, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

    if (jsonData.length === 0) return { headers: [], rows: [] };

    const headers = (jsonData[0] || []).map(h => String(h ?? ''));
    const rows = jsonData.slice(1).map(row =>
      (row || []).map(cell => String(cell ?? ''))
    );

    // Ensure all rows have the same number of columns as headers
    const normalizedRows = rows.map(row => {
      while (row.length < headers.length) {
        row.push('');
      }
      return row.slice(0, headers.length);
    });

    return { headers, rows: normalizedRows };
  } catch (e) {
    console.error('Error parsing Excel:', e);
    return { headers: [], rows: [] };
  }
};

// Check if file is Excel format
const isExcelFile = (mimeType: string): boolean => {
  return mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
         mimeType === 'application/vnd.ms-excel' ||
         mimeType.includes('spreadsheet') ||
         mimeType.includes('excel');
};

interface GeneratedFilePreviewProps {
  files?: GeneratedFile[];
  fileRefs?: FileRef[];
}

const GeneratedFilePreview: React.FC<GeneratedFilePreviewProps> = ({ files = [], fileRefs = [] }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<GeneratedFile | null>(null);
  const [selectedFileRef, setSelectedFileRef] = useState<FileRef | null>(null);
  const [selectedFileRefData, setSelectedFileRefData] = useState<ChatFileData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedHeaders, setEditedHeaders] = useState<string[]>([]);
  const [editedData, setEditedData] = useState<string[][]>([]);
  const [editedContent, setEditedContent] = useState('');
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const [loadingFileRef, setLoadingFileRef] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [blobObjectUrls, setBlobObjectUrls] = useState<Map<string, string>>(new Map());

  // Load blob object URLs for image file_refs (for inline preview)
  useEffect(() => {
    const loadImageBlobs = async () => {
      const imageRefs = fileRefs.filter(ref => ref.file_type === 'image');
      if (imageRefs.length === 0) return;

      try {
        const token = await getAccessTokenSilently();
        const newUrls = new Map(blobObjectUrls);
        for (const ref of imageRefs) {
          if (!newUrls.has(ref.file_id)) {
            try {
              const blob = await downloadChatFileBlob(ref.file_id, token);
              const url = URL.createObjectURL(blob);
              newUrls.set(ref.file_id, url);
            } catch {
              // Skip failed loads
            }
          }
        }
        setBlobObjectUrls(newUrls);
      } catch {
        // Auth error, skip
      }
    };

    loadImageBlobs();

    // Cleanup object URLs on unmount
    return () => {
      blobObjectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [fileRefs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if ((!files || files.length === 0) && (!fileRefs || fileRefs.length === 0)) return null;

  const canEdit = (file: GeneratedFile) => {
    return file.mime_type === 'text/csv' ||
           isExcelFile(file.mime_type) ||
           isTextDocument(file.mime_type) ||
           file.mime_type === 'text/plain';
  };

  const handleDownload = (file: GeneratedFile) => {
    const link = document.createElement('a');
    link.href = `data:${file.mime_type};base64,${file.content_base64}`;
    link.download = getFileName(file.path);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenModal = (file: GeneratedFile, startEditing: boolean = false) => {
    setSelectedFile(file);
    setModalOpen(true);
    setIsEditing(startEditing);

    if (file.mime_type === 'text/csv') {
      const { headers, rows } = parseCSV(file.content_base64);
      setEditedHeaders(headers);
      setEditedData(rows);
    } else if (isExcelFile(file.mime_type)) {
      const { headers, rows } = parseExcel(file.content_base64);
      setEditedHeaders(headers);
      setEditedData(rows);
    } else if (isTextDocument(file.mime_type) || file.mime_type === 'text/plain') {
      // Prefer parsed content; only fall back to atob() for plain text formats (never for binary DOCX)
      if ((file as any).content?.text) {
        setEditedContent(plainTextToEditorHtml((file as any).content.text));
      } else if (isDecodableAsText(file.mime_type)) {
        try {
          setEditedContent(atob(file.content_base64));
        } catch {
          setEditedContent('');
        }
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedFile(null);
    setIsEditing(false);
    setEditedHeaders([]);
    setEditedData([]);
    setEditedContent('');
  };

  const handleToggleEdit = () => {
    if (!isEditing && selectedFile) {
      if (selectedFile.mime_type === 'text/csv') {
        const { headers, rows } = parseCSV(selectedFile.content_base64);
        setEditedHeaders(headers);
        setEditedData(rows);
      } else if (isExcelFile(selectedFile.mime_type)) {
        const { headers, rows } = parseExcel(selectedFile.content_base64);
        setEditedHeaders(headers);
        setEditedData(rows);
      } else if (isTextDocument(selectedFile.mime_type) || selectedFile.mime_type === 'text/plain') {
        // Prefer parsed content; only fall back to atob() for plain text formats (never for binary DOCX)
        if ((selectedFile as any).content?.text) {
          setEditedContent(plainTextToEditorHtml((selectedFile as any).content.text));
        } else if (isDecodableAsText(selectedFile.mime_type)) {
          try {
            setEditedContent(atob(selectedFile.content_base64));
          } catch {
            setEditedContent('');
          }
        }
      }
    }
    setIsEditing(!isEditing);
  };

  const handleSpreadsheetChange = (headers: string[], data: string[][]) => {
    setEditedHeaders(headers);
    setEditedData(data);
  };

  const handleRichTextChange = (content: string) => {
    setEditedContent(content);
  };

  // --- FileRef handlers ---

  const handleOpenFileRef = async (ref: FileRef, startEditing: boolean = false) => {
    setSelectedFile(null);
    setSelectedFileRef(ref);
    setModalOpen(true);
    setIsEditing(startEditing);
    setLoadingFileRef(true);

    try {
      const token = await getAccessTokenSilently();
      const data = await fetchChatFile(ref.file_id, token);
      setSelectedFileRefData(data);

      // Populate editor state from content
      if (data.editable && data.content) {
        if (data.file_type === 'spreadsheet') {
          const sheets = data.content.sheets || {};
          const firstSheet = Object.values(sheets)[0] as any;
          if (firstSheet) {
            setEditedHeaders(firstSheet.headers || []);
            setEditedData(firstSheet.rows || []);
          }
        } else if (data.file_type === 'document') {
          setEditedContent(plainTextToEditorHtml(data.content.text || ''));
        }
      }

      // Load blob URL for images and PDFs (for modal display)
      if (data.file_type === 'image' || data.file_type === 'pdf') {
        if (!blobObjectUrls.has(ref.file_id)) {
          try {
            const blob = await downloadChatFileBlob(ref.file_id, token);
            const url = URL.createObjectURL(blob);
            setBlobObjectUrls(prev => new Map(prev).set(ref.file_id, url));
          } catch {
            // Failed to load blob
          }
        }
      }
    } catch (err) {
      console.error('Failed to load file ref:', err);
    } finally {
      setLoadingFileRef(false);
    }
  };

  const handleDownloadFileRef = async (ref: FileRef) => {
    try {
      const token = await getAccessTokenSilently();
      if (ref.editable) {
        // Export editable file to binary format
        const format = ref.file_type === 'spreadsheet' ? 'xlsx' : 'docx';
        const blob = await exportChatFile(ref.file_id, format, token);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const ext = ref.file_type === 'spreadsheet' ? 'xlsx' : 'docx';
        const baseName = ref.title.replace(/\.[^.]+$/, '');
        link.download = `${baseName}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Download binary file from GCS
        const blob = await downloadChatFileBlob(ref.file_id, token);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = ref.title;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download file ref:', err);
    }
  };

  const handleSaveFileRef = async () => {
    if (!selectedFileRef || !selectedFileRefData) return;

    setSavingEdit(true);
    setSaveSuccess(false);
    try {
      const token = await getAccessTokenSilently();
      let content: Record<string, any>;

      if (selectedFileRefData.file_type === 'spreadsheet') {
        // Get the sheet name from original content or default
        const originalSheets = selectedFileRefData.content?.sheets || {};
        const sheetName = Object.keys(originalSheets)[0] || 'Sheet1';
        content = {
          sheets: {
            [sheetName]: {
              headers: editedHeaders,
              rows: editedData,
            },
          },
        };
      } else {
        // Convert TipTap HTML back to plain text for consistent storage/export
        content = { text: editorHtmlToPlainText(editedContent) };
      }

      const result = await saveChatFileContent(selectedFileRef.file_id, content, token);
      if (result.success) {
        setSaveSuccess(true);
        // Update local data to reflect the save
        setSelectedFileRefData(prev => prev ? { ...prev, content, updated_at: result.updated_at } : prev);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (err) {
      console.error('Failed to save file:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleExportFileRef = async (format: string) => {
    if (!selectedFileRef) return;
    setExportMenuAnchor(null);
    try {
      const token = await getAccessTokenSilently();
      const blob = await exportChatFile(selectedFileRef.file_id, format, token);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const baseName = selectedFileRef.title.replace(/\.[^.]+$/, '');
      link.download = `${baseName}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export file:', err);
    }
  };

  const handleCloseFileRefModal = () => {
    setModalOpen(false);
    setSelectedFileRef(null);
    setSelectedFileRefData(null);
    setIsEditing(false);
    setEditedHeaders([]);
    setEditedData([]);
    setEditedContent('');
    setLoadingFileRef(false);
    setSavingEdit(false);
    setSaveSuccess(false);
  };

  const handleToggleEditFileRef = () => {
    if (!isEditing && selectedFileRefData?.content) {
      if (selectedFileRefData.file_type === 'spreadsheet') {
        const sheets = selectedFileRefData.content.sheets || {};
        const firstSheet = Object.values(sheets)[0] as any;
        if (firstSheet) {
          setEditedHeaders(firstSheet.headers || []);
          setEditedData(firstSheet.rows || []);
        }
      } else if (selectedFileRefData.file_type === 'document') {
        setEditedContent(plainTextToEditorHtml(selectedFileRefData.content.text || ''));
      }
    }
    setIsEditing(!isEditing);
  };

  const exportToCSV = () => {
    const csvContent = [
      editedHeaders.join(','),
      ...editedData.map(row => row.map(cell => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = selectedFile ? getFileName(selectedFile.path).replace(/\.[^.]+$/, '.csv') : 'export.csv';
    link.click();
    setExportMenuAnchor(null);
  };

  const exportToExcel = () => {
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheetData = [editedHeaders, ...editedData];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-size columns
    const colWidths = editedHeaders.map((header, i) => {
      const maxLength = Math.max(
        header.length,
        ...editedData.map(row => (row[i] || '').length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Generate and download
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = selectedFile ? getFileName(selectedFile.path).replace(/\.[^.]+$/, '.xlsx') : 'export.xlsx';
    link.click();
    setExportMenuAnchor(null);
  };

  const exportToDocx = () => {
    if (!selectedFile) return;
    // Download the original binary DOCX from base64
    const link = document.createElement('a');
    link.href = `data:${selectedFile.mime_type};base64,${selectedFile.content_base64}`;
    link.download = getFileName(selectedFile.path);
    link.click();
    setExportMenuAnchor(null);
  };

  const exportToText = () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editedContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = selectedFile ? getFileName(selectedFile.path).replace(/\.[^.]+$/, '.txt') : 'export.txt';
    link.click();
    setExportMenuAnchor(null);
  };

  const renderFilePreview = (file: GeneratedFile) => {
    const fileName = getFileName(file.path);
    const color = getFileColor(file.mime_type);
    const dataUrl = `data:${file.mime_type};base64,${file.content_base64}`;

    // Spreadsheet preview
    if (isSpreadsheet(file.mime_type) && file.mime_type === 'text/csv') {
      const { headers, rows } = parseCSV(file.content_base64);
      const previewRows = rows.slice(0, 5);
      const totalRows = rows.length;

      return (
        <FilePreviewContainer key={file.path}>
          <FileHeader $clickable onClick={() => handleOpenModal(file)}>
            <FileHeaderLeft>
              <FileIconWrapper $color={color}>
                {getFileIcon(file.mime_type)}
              </FileIconWrapper>
              <FileName>{fileName}</FileName>
            </FileHeaderLeft>
            <FileActions>
              <CustomTooltip title="Baixar" placement="top">
                <ActionButton onClick={(e) => { e.stopPropagation(); handleDownload(file); }}>
                  <DownloadIcon style={{ fontSize: 20 }} />
                </ActionButton>
              </CustomTooltip>
            </FileActions>
          </FileHeader>
          <PreviewContent>
            <TableWrapper>
              <PreviewTable>
                <thead>
                  <tr>
                    <TableHeader>#</TableHeader>
                    {headers.map((header, idx) => (
                      <TableHeader key={idx}>{header}</TableHeader>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rowIdx) => (
                    <TableRow key={rowIdx}>
                      <RowNumber>{rowIdx + 1}</RowNumber>
                      {row.map((cell, cellIdx) => (
                        <TableCell key={cellIdx}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </tbody>
              </PreviewTable>
            </TableWrapper>
          </PreviewContent>
          <PreviewFooter>
            Prévia com as {Math.min(5, totalRows)} primeiras linhas · Clique{' '}
            <ClickableText onClick={() => handleOpenModal(file)}>aqui</ClickableText>{' '}
            para expandir e editar
          </PreviewFooter>
        </FilePreviewContainer>
      );
    }

    // Non-CSV spreadsheet (Excel) - show preview with edit option
    if (isSpreadsheet(file.mime_type)) {
      const { headers, rows } = parseExcel(file.content_base64);
      const previewRows = rows.slice(0, 5);
      const totalRows = rows.length;

      return (
        <FilePreviewContainer key={file.path}>
          <FileHeader>
            <FileHeaderLeft>
              <FileIconWrapper $color={color}>
                {getFileIcon(file.mime_type)}
              </FileIconWrapper>
              <FileName>{fileName}</FileName>
            </FileHeaderLeft>
            <FileActions>
              <CustomTooltip title="Baixar" placement="top">
                <ActionButton onClick={() => handleDownload(file)}>
                  <DownloadIcon style={{ fontSize: 20 }} />
                </ActionButton>
              </CustomTooltip>
              <CustomTooltip title="Expandir e Editar" placement="top">
                <ActionButton onClick={() => handleOpenModal(file)}>
                  <OpenInFullIcon style={{ fontSize: 20 }} />
                </ActionButton>
              </CustomTooltip>
            </FileActions>
          </FileHeader>
          {headers.length > 0 ? (
            <>
              <PreviewContent>
                <TableWrapper>
                  <PreviewTable>
                    <thead>
                      <tr>
                        <TableHeader>#</TableHeader>
                        {headers.map((header, idx) => (
                          <TableHeader key={idx}>{header}</TableHeader>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                          <RowNumber>{rowIdx + 1}</RowNumber>
                          {row.map((cell, cellIdx) => (
                            <TableCell key={cellIdx}>{cell}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </tbody>
                  </PreviewTable>
                </TableWrapper>
              </PreviewContent>
              <PreviewFooter>
                Prévia com as {Math.min(5, totalRows)} primeiras linhas · Clique{' '}
                <ClickableText onClick={() => handleOpenModal(file)}>aqui</ClickableText>{' '}
                para expandir e editar
              </PreviewFooter>
            </>
          ) : (
            <>
              <FilePlaceholder $clickable onClick={() => handleOpenModal(file)}>
                <PlaceholderIcon $color={color}>
                  {getFileIcon(file.mime_type, 32)}
                </PlaceholderIcon>
                <PlaceholderText>Planilha Excel</PlaceholderText>
              </FilePlaceholder>
              <PreviewFooter>
                <ClickableText onClick={() => handleOpenModal(file)}>Clique aqui</ClickableText>{' '}
                para visualizar e editar
              </PreviewFooter>
            </>
          )}
        </FilePreviewContainer>
      );
    }

    // Image preview
    if (isImage(file.mime_type)) {
      return (
        <FilePreviewContainer $bare key={file.path}>
          <ImageBlock>
            <PreviewImage src={dataUrl} alt={fileName} onClick={() => handleOpenModal(file)} />
            <CustomTooltip title="Baixar" placement="top">
              <ImageDownloadButton onClick={(e) => { e.stopPropagation(); handleDownload(file); }} aria-label="Baixar">
                <DownloadIcon style={{ fontSize: 18 }} />
              </ImageDownloadButton>
            </CustomTooltip>
          </ImageBlock>
        </FilePreviewContainer>
      );
    }

    // PDF and other files - placeholder
    return (
      <FilePreviewContainer key={file.path}>
        <FileHeader $clickable onClick={() => handleOpenModal(file)}>
          <FileHeaderLeft>
            <FileIconWrapper $color={color}>
              {getFileIcon(file.mime_type)}
            </FileIconWrapper>
            <FileName>{fileName}</FileName>
            {canEdit(file) && (
              <span style={{ fontSize: '0.7rem', color: '#60a5fa', marginLeft: 8 }}>
                (Editável)
              </span>
            )}
          </FileHeaderLeft>
          <FileActions>
            <CustomTooltip title="Baixar" placement="top">
              <ActionButton onClick={(e) => { e.stopPropagation(); handleDownload(file); }}>
                <DownloadIcon style={{ fontSize: 20 }} />
              </ActionButton>
            </CustomTooltip>
            {canEdit(file) && (
              <CustomTooltip title="Editar" placement="top">
                <ActionButton onClick={(e) => { e.stopPropagation(); handleOpenModal(file, true); }}>
                  <EditIcon style={{ fontSize: 20 }} />
                </ActionButton>
              </CustomTooltip>
            )}
          </FileActions>
        </FileHeader>
      </FilePreviewContainer>
    );
  };

  // Render a file_ref inline preview (similar to renderFilePreview but for FileRef)
  const renderFileRefPreview = (ref: FileRef) => {
    const color = getFileColor(ref.mime_type);
    const icon = getFileIcon(ref.mime_type);

    // Image preview via blob object URL
    if (ref.file_type === 'image') {
      const blobUrl = blobObjectUrls.get(ref.file_id);
      return (
        <FilePreviewContainer $bare key={ref.file_id}>
          {blobUrl ? (
            <ImageBlock>
              <PreviewImage src={blobUrl} alt={ref.title} onClick={() => handleOpenFileRef(ref)} />
              <CustomTooltip title="Baixar" placement="top">
                <ImageDownloadButton onClick={(e) => { e.stopPropagation(); handleDownloadFileRef(ref); }} aria-label="Baixar">
                  <DownloadIcon style={{ fontSize: 18 }} />
                </ImageDownloadButton>
              </CustomTooltip>
            </ImageBlock>
          ) : (
            <FilePlaceholder>
              <CircularProgress size={24} />
            </FilePlaceholder>
          )}
        </FilePreviewContainer>
      );
    }

    // Editable files (spreadsheet/document) and binary files (PDF)
    return (
      <FilePreviewContainer key={ref.file_id}>
        <FileHeader $clickable onClick={() => handleOpenFileRef(ref)}>
          <FileHeaderLeft>
            <FileIconWrapper $color={color}>{icon}</FileIconWrapper>
            <FileName>{ref.title}</FileName>
            {ref.editable && (
              <span style={{ fontSize: '0.7rem', color: '#60a5fa', marginLeft: 8 }}>
                (Editável)
              </span>
            )}
          </FileHeaderLeft>
          <FileActions>
            <CustomTooltip title="Baixar" placement="top">
              <ActionButton onClick={(e) => { e.stopPropagation(); handleDownloadFileRef(ref); }}>
                <DownloadIcon style={{ fontSize: 20 }} />
              </ActionButton>
            </CustomTooltip>
            {ref.editable && (
              <CustomTooltip title="Editar" placement="top">
                <ActionButton onClick={(e) => { e.stopPropagation(); handleOpenFileRef(ref, true); }}>
                  <EditIcon style={{ fontSize: 20 }} />
                </ActionButton>
              </CustomTooltip>
            )}
          </FileActions>
        </FileHeader>
      </FilePreviewContainer>
    );
  };

  // Render modal content for file_ref items
  const renderFileRefModalContent = () => {
    if (!selectedFileRef) return null;

    if (loadingFileRef) {
      return (
        <FilePlaceholder style={{ minHeight: 300 }}>
          <CircularProgress size={40} />
          <PlaceholderText style={{ marginTop: 16 }}>Carregando arquivo...</PlaceholderText>
        </FilePlaceholder>
      );
    }

    if (!selectedFileRefData) {
      return (
        <FilePlaceholder style={{ minHeight: 300 }}>
          <PlaceholderText>Erro ao carregar arquivo</PlaceholderText>
        </FilePlaceholder>
      );
    }

    const data = selectedFileRefData;

    // Spreadsheet
    if (data.file_type === 'spreadsheet' && data.content) {
      if (isEditing) {
        return (
          <EditorContent>
            <SpreadsheetEditor
              initialHeaders={editedHeaders}
              initialData={editedData}
              onChange={handleSpreadsheetChange}
            />
          </EditorContent>
        );
      }

      const sheets = data.content.sheets || {};
      const firstSheet = Object.values(sheets)[0] as any;
      if (!firstSheet) return null;

      return (
        <ModalTableWrapper>
          <PreviewTable>
            <thead>
              <tr>
                <TableHeader>#</TableHeader>
                {(firstSheet.headers || []).map((header: string, idx: number) => (
                  <TableHeader key={idx}>{header}</TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {(firstSheet.rows || []).map((row: any[], rowIdx: number) => (
                <TableRow key={rowIdx}>
                  <RowNumber>{rowIdx + 1}</RowNumber>
                  {row.map((cell: any, cellIdx: number) => (
                    <TableCell key={cellIdx}>{cell != null ? String(cell) : ''}</TableCell>
                  ))}
                </TableRow>
              ))}
            </tbody>
          </PreviewTable>
        </ModalTableWrapper>
      );
    }

    // Document
    if (data.file_type === 'document' && data.content) {
      if (isEditing) {
        return (
          <EditorContent>
            <RichTextEditor
              initialContent={editedContent}
              onChange={handleRichTextChange}
            />
          </EditorContent>
        );
      }

      return (
        <ModalTableWrapper style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', lineHeight: 1.8 }}>
            {data.content.text || ''}
          </div>
        </ModalTableWrapper>
      );
    }

    // Image
    if (data.file_type === 'image') {
      const blobUrl = blobObjectUrls.get(data.file_id);
      if (!blobUrl) {
        return (
          <FilePlaceholder style={{ minHeight: 300 }}>
            <CircularProgress size={40} />
            <PlaceholderText style={{ marginTop: 16 }}>Carregando imagem...</PlaceholderText>
          </FilePlaceholder>
        );
      }
      return (
        <ModalImageWrapper>
          <ModalImage src={blobUrl} alt={data.title} />
        </ModalImageWrapper>
      );
    }

    // PDF
    if (data.file_type === 'pdf') {
      const blobUrl = blobObjectUrls.get(data.file_id);
      if (!blobUrl) {
        return (
          <FilePlaceholder style={{ minHeight: 300 }}>
            <CircularProgress size={40} />
            <PlaceholderText style={{ marginTop: 16 }}>Carregando PDF...</PlaceholderText>
          </FilePlaceholder>
        );
      }
      return (
        <iframe
          src={blobUrl}
          style={{ width: '100%', height: '70vh', border: 'none' }}
          title={data.title}
        />
      );
    }

    // Fallback
    const color = getFileColor(data.mime_type);
    return (
      <FilePlaceholder style={{ minHeight: 300 }}>
        <PlaceholderIcon $color={color}>
          {getFileIcon(data.mime_type, 48)}
        </PlaceholderIcon>
        <PlaceholderText style={{ marginBottom: 16 }}>
          Este tipo de arquivo não pode ser visualizado no navegador
        </PlaceholderText>
        <ActionButton
          onClick={() => handleDownloadFileRef(selectedFileRef)}
          style={{ background: color + '20', color: color }}
        >
          <DownloadIcon style={{ marginRight: 8 }} />
          Baixar arquivo
        </ActionButton>
      </FilePlaceholder>
    );
  };

  const renderModalContent = () => {
    if (!selectedFile) return null;

    const dataUrl = `data:${selectedFile.mime_type};base64,${selectedFile.content_base64}`;

    // CSV or Excel in modal - spreadsheet editor or view
    if (selectedFile.mime_type === 'text/csv' || isExcelFile(selectedFile.mime_type)) {
      if (isEditing) {
        return (
          <EditorContent>
            <SpreadsheetEditor
              initialHeaders={editedHeaders}
              initialData={editedData}
              onChange={handleSpreadsheetChange}
            />
          </EditorContent>
        );
      }

      const { headers, rows } = selectedFile.mime_type === 'text/csv'
        ? parseCSV(selectedFile.content_base64)
        : parseExcel(selectedFile.content_base64);

      return (
        <ModalTableWrapper>
          <PreviewTable>
            <thead>
              <tr>
                <TableHeader>#</TableHeader>
                {headers.map((header, idx) => (
                  <TableHeader key={idx}>{header}</TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <TableRow key={rowIdx}>
                  <RowNumber>{rowIdx + 1}</RowNumber>
                  {row.map((cell, cellIdx) => (
                    <TableCell key={cellIdx}>{cell}</TableCell>
                  ))}
                </TableRow>
              ))}
            </tbody>
          </PreviewTable>
        </ModalTableWrapper>
      );
    }

    // Text documents - rich text editor or view
    if (isTextDocument(selectedFile.mime_type) || selectedFile.mime_type === 'text/plain') {
      // Get content from parsed data (preferred) or decode base64 (only for actual text, never binary DOCX)
      let textContent = '';
      if ((selectedFile as any).content?.text) {
        // Use pre-parsed content from backend
        textContent = (selectedFile as any).content.text;
      } else if (selectedFile.content_base64 && isDecodableAsText(selectedFile.mime_type)) {
        try {
          textContent = atob(selectedFile.content_base64);
        } catch {
          textContent = '';
        }
      }

      if (isEditing) {
        return (
          <EditorContent>
            <RichTextEditor
              initialContent={editedContent || textContent}
              onChange={handleRichTextChange}
            />
          </EditorContent>
        );
      }

      return (
        <ModalTableWrapper style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', lineHeight: 1.8 }}>
            {textContent}
          </div>
        </ModalTableWrapper>
      );
    }

    // Image in modal
    if (isImage(selectedFile.mime_type)) {
      return (
        <ModalImageWrapper>
          <ModalImage src={dataUrl} alt={getFileName(selectedFile.path)} />
        </ModalImageWrapper>
      );
    }

    // PDF in modal - use iframe
    if (selectedFile.mime_type === 'application/pdf') {
      return (
        <iframe
          src={dataUrl}
          style={{ width: '100%', height: '70vh', border: 'none' }}
          title={getFileName(selectedFile.path)}
        />
      );
    }

    // Other files - show download prompt
    const color = getFileColor(selectedFile.mime_type);
    return (
      <FilePlaceholder style={{ minHeight: 300 }}>
        <PlaceholderIcon $color={color}>
          {getFileIcon(selectedFile.mime_type, 48)}
        </PlaceholderIcon>
        <PlaceholderText style={{ marginBottom: 16 }}>
          Este tipo de arquivo não pode ser visualizado no navegador
        </PlaceholderText>
        <ActionButton
          onClick={() => handleDownload(selectedFile)}
          style={{ background: color + '20', color: color }}
        >
          <DownloadIcon style={{ marginRight: 8 }} />
          Baixar arquivo
        </ActionButton>
      </FilePlaceholder>
    );
  };

  // Determine which close handler and modal content to use
  const isFileRefModal = selectedFileRef !== null;
  const activeCloseHandler = isFileRefModal ? handleCloseFileRefModal : handleCloseModal;
  const activeFileName = isFileRefModal ? (selectedFileRef?.title || '') : (selectedFile ? getFileName(selectedFile.path) : '');
  const activeMimeType = isFileRefModal ? (selectedFileRef?.mime_type || '') : (selectedFile?.mime_type || '');
  const activeCanEdit = isFileRefModal ? (selectedFileRef?.editable || false) : (selectedFile ? canEdit(selectedFile) : false);
  const activeToggleEdit = isFileRefModal ? handleToggleEditFileRef : handleToggleEdit;

  return (
    <>
      {files.map(renderFilePreview)}
      {fileRefs.map(renderFileRefPreview)}

      <Modal
        open={modalOpen}
        onClose={activeCloseHandler}
        aria-labelledby="file-preview-modal"
      >
        {isEditing ? (
          <EditorModalContainer>
            <ModalHeader>
              <ModalTitle>
                <FileIconWrapper $color={getFileColor(activeMimeType)}>
                  {getFileIcon(activeMimeType)}
                </FileIconWrapper>
                <ModalFileName>{activeFileName}</ModalFileName>
                <span style={{ fontSize: '0.8rem', color: '#60a5fa', marginLeft: 8 }}>
                  (Editando)
                </span>
              </ModalTitle>
              <FileActions>
                {/* Save button for file_ref editable files */}
                {isFileRefModal && selectedFileRef?.editable && (
                  <CustomTooltip title={saveSuccess ? "Salvo!" : "Salvar"} placement="top">
                    <ActionButton
                      onClick={handleSaveFileRef}
                      disabled={savingEdit}
                      style={saveSuccess ? { color: '#22c55e' } : undefined}
                    >
                      {savingEdit ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SaveIcon style={{ fontSize: 20 }} />
                      )}
                    </ActionButton>
                  </CustomTooltip>
                )}
                {/* Generated files are ephemeral (no server file to persist to),
                    so "save" = download the edited file. Show a Salvar button that
                    opens the export/download menu. */}
                {isEditing && !isFileRefModal && (
                  <CustomTooltip title="Salvar" placement="top">
                    <ActionButton onClick={(e) => setExportMenuAnchor(e.currentTarget)}>
                      <SaveIcon style={{ fontSize: 20 }} />
                    </ActionButton>
                  </CustomTooltip>
                )}
                {isEditing && isFileRefModal && (
                  <CustomTooltip title="Exportar" placement="top">
                    <ActionButton onClick={(e) => setExportMenuAnchor(e.currentTarget)}>
                      <FileDownloadIcon style={{ fontSize: 20 }} />
                    </ActionButton>
                  </CustomTooltip>
                )}
                {activeCanEdit && (
                  <CustomTooltip title={isEditing ? "Visualizar" : "Editar"} placement="top">
                    <ActionButton onClick={activeToggleEdit}>
                      {isEditing ? <VisibilityIcon style={{ fontSize: 20 }} /> : <EditIcon style={{ fontSize: 20 }} />}
                    </ActionButton>
                  </CustomTooltip>
                )}
                <CustomTooltip title="Fechar" placement="top">
                  <ActionButton onClick={activeCloseHandler}>
                    <CloseIcon style={{ fontSize: 20 }} />
                  </ActionButton>
                </CustomTooltip>
              </FileActions>
            </ModalHeader>
            <ModalContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {isFileRefModal ? renderFileRefModalContent() : renderModalContent()}
            </ModalContent>
          </EditorModalContainer>
        ) : (
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>
                <FileIconWrapper $color={getFileColor(activeMimeType)}>
                  {getFileIcon(activeMimeType)}
                </FileIconWrapper>
                <ModalFileName>{activeFileName}</ModalFileName>
              </ModalTitle>
              <FileActions>
                {activeCanEdit && (
                  <CustomTooltip title="Editar" placement="top">
                    <ActionButton onClick={activeToggleEdit}>
                      <EditIcon style={{ fontSize: 20 }} />
                    </ActionButton>
                  </CustomTooltip>
                )}
                {!isFileRefModal && selectedFile && (
                  <CustomTooltip title="Baixar" placement="top">
                    <ActionButton onClick={() => handleDownload(selectedFile)}>
                      <DownloadIcon style={{ fontSize: 20 }} />
                    </ActionButton>
                  </CustomTooltip>
                )}
                {isFileRefModal && selectedFileRef && (
                  <CustomTooltip title="Baixar" placement="top">
                    <ActionButton onClick={() => handleDownloadFileRef(selectedFileRef)}>
                      <DownloadIcon style={{ fontSize: 20 }} />
                    </ActionButton>
                  </CustomTooltip>
                )}
                <CustomTooltip title="Fechar" placement="top">
                  <ActionButton onClick={activeCloseHandler}>
                    <CloseIcon style={{ fontSize: 20 }} />
                  </ActionButton>
                </CustomTooltip>
              </FileActions>
            </ModalHeader>
            <ModalContent>
              {isFileRefModal ? renderFileRefModalContent() : renderModalContent()}
            </ModalContent>
          </ModalContainer>
        )}
      </Modal>

      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        {/* Export options for file_ref items */}
        {isFileRefModal && selectedFileRef?.file_type === 'spreadsheet' ? (
          <>
            <ExportMenuItem onClick={() => handleExportFileRef('csv')}>
              <ListItemIcon>
                <TableChartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Exportar como CSV" secondary=".csv" />
            </ExportMenuItem>
            <ExportMenuItem onClick={() => handleExportFileRef('xlsx')}>
              <ListItemIcon>
                <TableChartIcon fontSize="small" style={{ color: '#22c55e' }} />
              </ListItemIcon>
              <ListItemText primary="Exportar como Excel" secondary=".xlsx" />
            </ExportMenuItem>
          </>
        ) : isFileRefModal && selectedFileRef?.file_type === 'document' ? (
          <>
            <ExportMenuItem onClick={() => handleExportFileRef('docx')}>
              <ListItemIcon>
                <ArticleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Exportar como DOCX" secondary=".docx" />
            </ExportMenuItem>
            <ExportMenuItem onClick={() => handleExportFileRef('txt')}>
              <ListItemIcon>
                <ArticleIcon fontSize="small" style={{ color: '#8b5cf6' }} />
              </ListItemIcon>
              <ListItemText primary="Exportar como Texto" secondary=".txt" />
            </ExportMenuItem>
          </>
        ) : (selectedFile?.mime_type === 'text/csv' || (selectedFile && isExcelFile(selectedFile.mime_type))) ? (
          /* Legacy export options for base64 files */
          <>
            <ExportMenuItem onClick={exportToCSV}>
              <ListItemIcon>
                <TableChartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Exportar como CSV" secondary=".csv" />
            </ExportMenuItem>
            <ExportMenuItem onClick={exportToExcel}>
              <ListItemIcon>
                <TableChartIcon fontSize="small" style={{ color: '#22c55e' }} />
              </ListItemIcon>
              <ListItemText primary="Exportar como Excel" secondary=".xlsx" />
            </ExportMenuItem>
          </>
        ) : (
          <>
            <ExportMenuItem onClick={exportToDocx}>
              <ListItemIcon>
                <ArticleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Exportar como DOCX" secondary=".docx" />
            </ExportMenuItem>
            <ExportMenuItem onClick={exportToText}>
              <ListItemIcon>
                <ArticleIcon fontSize="small" style={{ color: '#8b5cf6' }} />
              </ListItemIcon>
              <ListItemText primary="Exportar como Texto" secondary=".txt" />
            </ExportMenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default GeneratedFilePreview;
